/**
 * @file RagJobCard.jsx
 * @description Individual folder job card showing progress.
 * Displays folder name, status, progress bar, and error messages.
 */

import React from 'react';
import PropTypes from 'prop-types';
import {
  Card,
  CardContent,
  Typography,
  LinearProgress,
  Box,
  Chip
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  HourglassEmpty as QueuedIcon,
  Sync as ProcessingIcon,
  Cancel as CancelIcon,
  WarningAmber as WarningAmberIcon
} from '@mui/icons-material';

// Etiquetas de stage del ping intra-fichero (#251/rag-notification-layer).
// Duplicado a propósito de CollectionCard.jsx:50 — objeto de 2 claves, no
// justifica un módulo compartido.
const STAGE_LABELS = { analyzing_images: 'analizando imágenes', embedding: 'indexando' };

// #1006 — cuando el padre no pasa resolutor. La tarjeta NO llama al hook ella misma
// (mismo criterio que `FileThumbnail`): así sigue siendo un componente de presentación
// puro, testeable sin AuthContext ni fetch del catálogo.
const FALLBACK_ERROR_TEXT = 'No se pudo procesar la carpeta';

const RagJobCard = ({ folderJob, theme, masterCancelled = false, liveProgressDetail = null, resolveRagError = null }) => {
  const {
    folder_name: folderName,
    status,
    processed_files: processedFiles = 0,
    total_files: totalFiles = 0,
    error_code: errorCode
  } = folderJob;

  // Calculate progress percentage
  const progress = totalFiles > 0 ? (processedFiles / totalFiles) * 100 : 0;

  // Status configuration
  const statusConfig = {
    pending: {
      icon: <QueuedIcon fontSize="small" />,
      color: 'default',
      label: 'Pendiente'
    },
    queued: {
      icon: <QueuedIcon fontSize="small" />,
      color: 'default',
      label: 'En cola'
    },
    processing: {
      icon: (
        <ProcessingIcon
          fontSize="small"
          sx={{
            animation: 'ragJobSpin 1.6s linear infinite',
            '@keyframes ragJobSpin': {
              from: { transform: 'rotate(0deg)' },
              to: { transform: 'rotate(360deg)' }
            }
          }}
        />
      ),
      color: 'primary',
      label: 'Procesando'
    },
    completed: {
      icon: <CheckCircleIcon fontSize="small" />,
      color: 'success',
      label: 'Completado'
    },
    failed: {
      icon: <ErrorIcon fontSize="small" />,
      color: 'error',
      label: 'Error'
    },
    cancelled: {
      icon: <WarningAmberIcon fontSize="small" />,
      color: 'warning',
      label: 'Cancelado'
    },
    interrupted: {
      icon: <WarningAmberIcon fontSize="small" />,
      color: 'warning',
      label: 'Interrumpido'
    }
  };

  // If master job was cancelled and this folder was still processing/queued/pending, show as interrupted
  const effectiveStatus = masterCancelled && (status === 'processing' || status === 'queued' || status === 'pending')
    ? 'interrupted'
    : status;

  const currentStatus = statusConfig[effectiveStatus] || statusConfig.queued;

  // No hay telemetría intra-fichero en este job (carpeta/master, Sistema A): mientras
  // processedFiles=0 una barra determinate al 0% parece colgada, sobre todo con
  // totalFiles=1 (un solo documento grande puede tardar minutos en su primer avance).
  // Indeterminate + texto honesto comunica actividad real sin prometer un % que no se mide.
  const isExtractingFirstFile = effectiveStatus === 'processing' && processedFiles === 0;

  // Detalle de lote en vivo (issue #255) — stage desconocido o sin dato → sin sufijo.
  const stageLabel = liveProgressDetail ? STAGE_LABELS[liveProgressDetail.stage] : null;
  const liveDetailText = stageLabel
    ? ` · ${stageLabel}${Number.isInteger(liveProgressDetail.batch)
        ? ` (lote ${liveProgressDetail.batch}${Number.isInteger(liveProgressDetail.batchTotal) ? `/${liveProgressDetail.batchTotal}` : ''})`
        : ''}`
    : '';

  return (
    <Card
      variant="outlined"
      sx={{
        mb: 1,
        borderLeft: `4px solid ${
          effectiveStatus === 'completed'
            ? theme.palette.success.main
            : effectiveStatus === 'failed'
            ? theme.palette.error.main
            : effectiveStatus === 'processing'
            ? theme.palette.primary.main
            : effectiveStatus === 'interrupted'
            ? theme.palette.warning.main
            : theme.palette.grey[400]
        }`
      }}
    >
      <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
        {/* Header: Folder name + Status chip */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 1
          }}
        >
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              flex: 1,
              mr: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {folderName}
          </Typography>

          <Chip
            icon={currentStatus.icon}
            label={currentStatus.label}
            size="small"
            color={currentStatus.color}
            sx={{ minWidth: 110 }}
          />
        </Box>

        {/* Progress bar (show for processing and interrupted) */}
        {(effectiveStatus === 'processing' || effectiveStatus === 'interrupted') && (
          <Box sx={{ mb: 0.5 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 0.5
              }}
            >
              <Typography variant="caption" color="text.secondary">
                {(isExtractingFirstFile ? 'Extrayendo contenido…' : `${processedFiles} / ${totalFiles} archivos`) + liveDetailText}
              </Typography>
              {!isExtractingFirstFile && (
                <Typography variant="caption" color="text.secondary">
                  {Math.round(progress)}%
                </Typography>
              )}
            </Box>
            <LinearProgress
              variant={isExtractingFirstFile ? 'indeterminate' : 'determinate'}
              value={progress}
              color={effectiveStatus === 'interrupted' ? 'warning' : 'primary'}
              sx={{ height: 6, borderRadius: 1 }}
            />
          </Box>
        )}

        {/* Completed summary */}
        {effectiveStatus === 'completed' && totalFiles > 0 && (
          <Typography variant="caption" color="text.secondary">
            {totalFiles} archivos procesados
          </Typography>
        )}

        {/* Cancelled summary */}
        {effectiveStatus === 'cancelled' && folderJob.completed_folders != null && (
          <Typography variant="caption" color="text.secondary">
            {folderJob.completed_folders} de {folderJob.total_folders} carpetas · {folderJob.processed_files || 0} archivos
          </Typography>
        )}

        {/* Error message */}
        {effectiveStatus === 'failed' && (
          <Typography
            variant="caption"
            color="error"
            sx={{
              display: 'block',
              mt: 0.5,
              fontStyle: 'italic'
            }}
          >
            {resolveRagError ? resolveRagError(errorCode).message : FALLBACK_ERROR_TEXT}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

RagJobCard.propTypes = {
  folderJob: PropTypes.shape({
    id: PropTypes.string.isRequired,
    folder_name: PropTypes.string.isRequired,
    status: PropTypes.oneOf(['queued', 'pending', 'processing', 'completed', 'failed', 'cancelled']).isRequired,
    processed_files: PropTypes.number,
    total_files: PropTypes.number,
    error_code: PropTypes.string
  }).isRequired,
  theme: PropTypes.object.isRequired,
  masterCancelled: PropTypes.bool,
  liveProgressDetail: PropTypes.shape({
    stage: PropTypes.string,
    batch: PropTypes.number,
    batchTotal: PropTypes.number
  }),
  // (code) => { message, action, retryable } — lo inyecta RagProgressPanel desde
  // useRagErrorCodes. Opcional: sin él la tarjeta cae a un texto genérico (#1006).
  resolveRagError: PropTypes.func
};

export default RagJobCard;
